import clsx from 'clsx';
import * as React from 'react';
import { useObjectRef } from 'react-aria';
import { type ContextValue, useContextProps } from 'react-aria-components';

import { useAutoFocus } from '@gitbook/util-react-hooks';

import { useFieldProps } from '../Form';

export type HeadlessTextInputProps = Omit<React.ComponentPropsWithoutRef<'input'>, 'size'> & {
    /**
     * Class name for the input element.
     */
    inputClassName?: string;
    /**
     * Placeholder text is required to ensure good UX for unstyled inputs
     */
    placeholder: string;
    /**
     * Make the text input multiline.
     * @default false
     */
    multiline?: boolean;
};

export const HeadlessTextInputContext = React.createContext<
    ContextValue<HeadlessTextInputProps, HTMLInputElement>
>({});

/**
 * An unstyled input component that can be used to build your own input component, mainly used by `TextInput`.
 */
export const HeadlessTextInput = React.forwardRef(function HeadlessTextInput(
    props: HeadlessTextInputProps,
    forwardedRef: React.ForwardedRef<HTMLInputElement>
) {
    [props, forwardedRef] = useContextProps(props, forwardedRef, HeadlessTextInputContext);
    const { inputClassName, multiline, ...inputProps } = props;

    const inputRef = useObjectRef(forwardedRef);
    useAutoFocus(inputRef, props.autoFocus ?? false);
    const { ref, className, ...fieldProps } = useFieldProps({
        ref: inputRef,
        ...inputProps,
        type: props.type ?? 'text',
        autoComplete: props.autoComplete ?? 'off',
    });

    if (multiline) {
        return (
            // @ts-expect-error - We can consider input and textarea as roughly equivalent.
            <textarea
                ref={ref}
                data-1p-ignore
                {...fieldProps}
                className={clsx(
                    'm-0 min-w-0 flex-1 bg-transparent p-0 text-base placeholder:text-light',
                    inputClassName
                )}
            />
        );
    }

    return (
        <input
            ref={ref}
            data-1p-ignore
            {...fieldProps}
            className={clsx(
                'm-0 min-w-0 flex-1 bg-transparent p-0 text-base placeholder:text-light',
                inputClassName
            )}
        />
    );
});
